import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import apiClient from '../api';

function Breadcrumbs() {
  const location = useLocation();
  const params = useParams();
  const [juvenileName, setJuvenileName] = useState('');

  useEffect(() => {
    if (params.id) {
      const fetchName = async () => {
        try {
          const response = await apiClient.get(`/api/juveniles/${params.id}`);
          const { first_name, last_name } = response.data;
          setJuvenileName(`${first_name} ${last_name}`);
        } catch (err) {
          console.error("Failed to fetch juvenile name for breadcrumbs", err);
        }
      };
      fetchName();
    }
  }, [params.id]);

  const pathnames = location.pathname.split('/').filter((x) => x);

  if (pathnames.length <= 1 && pathnames[0] === 'juveniles') {
    return null;
  }

  return (
    <nav aria-label="breadcrumb" className="mb-2">
      <ol className="flex items-center gap-2">
        <li>
          <Link 
            to="/juveniles" 
            className="inline-flex items-center px-3 py-1.5 bg-slate-100 hover:bg-yellow-100 border border-slate-200 hover:border-yellow-300 rounded-lg text-sm font-medium text-slate-700 hover:text-yellow-800 transition-all duration-200 ease-in-out"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Juveniles
          </Link>
        </li>
        {params.id && (
          <>
            <li>
              <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </li>
            <li>
              {juvenileName ? (
                pathnames.includes('edit') ? (
                  <Link 
                    to={`/juveniles/${params.id}`} 
                    className="inline-flex items-center px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 hover:border-blue-300 rounded-lg text-sm font-medium text-blue-700 hover:text-blue-800 transition-all duration-200 ease-in-out"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {juvenileName}
                  </Link>
                ) : (
                  <span className="inline-flex items-center px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg text-sm font-semibold text-yellow-800">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {juvenileName}
                  </span>
                )
              ) : (
                <span className="inline-flex items-center px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-500">
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              )}
            </li>
          </>
        )}
        {pathnames.includes('edit') && (
          <>
            <li>
              <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </li>
            <li>
              <span className="inline-flex items-center px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-sm font-semibold text-green-800">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </span>
            </li>
          </>
        )}
        {pathnames.includes('new') && (
          <>
            <li>
              <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </li>
            <li>
              <span className="inline-flex items-center px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-sm font-semibold text-purple-800">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                New Profile
              </span>
            </li>
          </>
        )}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;